import { memo } from "react";

export interface SwatchCardProps {
  shadeCode: string;
  name: string;
  hex: string;
  isSelected?: boolean;
  onSelect: (hex: string, shadeCode: string) => void;
}

/**
 * Renders one paint swatch.
 *
 * Deliberately renders color as a CSS `background-color`, never as an
 * image file. This matters for cross-device performance parity: 150 image
 * requests (even small ones) is 150 network round-trips and 150 decode
 * operations that scale badly on a mid-range phone's CPU/network, while
 * 150 solid-color divs cost the browser almost nothing regardless of
 * device class. The catalog is exactly as fast on a 3-year-old Android
 * phone as on a desktop because there's no asset pipeline in the critical
 * path at all.
 */
function SwatchCardImpl({ shadeCode, name, hex, isSelected, onSelect }: SwatchCardProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(hex, shadeCode)}
      aria-pressed={isSelected}
      aria-label={`${name}, shade ${shadeCode}, color ${hex}`}
      className={[
        "group relative flex flex-col overflow-hidden rounded-t-md",
        "border border-[#D9D5CC] bg-[#F7F6F3]",
        // min touch target regardless of viewport — see design-tokens.ts
        "min-h-[96px] w-full",
        "transition-transform duration-150 ease-out",
        "motion-safe:hover:-rotate-1 motion-safe:hover:-translate-y-0.5",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
        "focus-visible:outline-[#52606D]",
        isSelected ? "ring-2 ring-[#52606D] ring-offset-1" : "",
        // lets the browser skip layout/paint work for offscreen cards —
        // the single biggest lever for keeping a 150-item grid smooth on
        // low-power devices without hand-rolling virtualization
        "[content-visibility:auto] [contain-intrinsic-size:120px]",
      ].join(" ")}
    >
      <span
        className="block h-16 w-full"
        style={{ backgroundColor: hex }}
        aria-hidden="true"
      />
      <span className="flex flex-col gap-0.5 px-2 py-1.5 text-left">
        <span className="truncate text-[13px] font-medium text-[#232220]">
          {name}
        </span>
        <span className="font-mono text-[11px] text-[#6B6660]">{shadeCode}</span>
      </span>
    </button>
  );
}

// Re-render only when this card's own props actually change — with 150
// cards on screen, avoiding needless re-renders on every parent state
// change (e.g. filter changes, scroll) is what keeps interaction snappy
// on lower-powered devices.
export const SwatchCard = memo(SwatchCardImpl, (prev, next) => {
  return (
    prev.hex === next.hex &&
    prev.shadeCode === next.shadeCode &&
    prev.name === next.name &&
    prev.isSelected === next.isSelected
  );
});
